import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import styles from './BCMembersPage.module.css';

/**
 * The Business Central member directory.
 *
 * /members shows the ~42 people who signed up in the app. This shows the actual
 * customer base — the 23,481 contacts BC holds, 5,664 of them VIP — so the store
 * can see its own members rather than only its app installs.
 *
 * Read-only on purpose: these are directory rows, not accounts. BC stays where a
 * member is edited, and nobody can log in as one.
 */

const SCHEMES = [
  { value: '',       label: 'All schemes' },
  { value: 'VIP',    label: 'VIP' },
  { value: 'SILVER', label: 'Silver' },
];

const APP_FILTERS = [
  { value: '',    label: 'Everyone' },
  { value: 'no',  label: 'No app account' },
  { value: 'yes', label: 'Has the app' },
];

const EXPIRY_FILTERS = [
  { value: '',        label: 'Any expiry' },
  { value: 'active',  label: 'Still valid' },
  { value: 'soon',    label: 'Expiring in 60 days' },
  { value: 'expired', label: 'Expired' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : '—');

export default function BCMembersPage() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [scheme, setScheme] = useState('');
  const [hasApp, setHasApp] = useState('');
  const [expiry, setExpiry] = useState('');

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      // The axios instance has a response interceptor returning r.data, so the
      // payload arrives directly — destructuring { data } yields undefined.
      const s = await adminAPI.getBCMemberStats();
      setStats(s);
    } catch (e) {
      console.error('[bc-members] stats failed', e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getBCMembers({ search, scheme, hasApp, expiry, page, limit: 25 });
      setRows(res.members || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      // The interceptor rejects with a plain Error, so e.message carries the
      // server's message when there was one.
      console.error('[bc-members] load failed', e);
      setError(e.message || 'Could not load the directory.');
    } finally {
      setLoading(false);
    }
  }, [search, scheme, hasApp, expiry, page]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Debounced, so typing in the search box does not fire a query per keystroke
  // against a 23k-row table.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Filters change the result set, so go back to the first page.
  useEffect(() => { setPage(1); }, [search, scheme, hasApp, expiry]);

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    try {
      await adminAPI.syncBCMembers();
      await Promise.all([loadStats(), load()]);
    } catch (e) {
      setError(e.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Business Central Members</h1>
          <p className={styles.subtitle}>
            {total.toLocaleString()} matching
            {stats ? ` of ${stats.total.toLocaleString()} in BC` : ''}
            {stats?.last_synced_at && (
              <>
                <span className={styles.subtleDot}>·</span>
                synced {new Date(stats.last_synced_at).toLocaleString('en-GB')}
              </>
            )}
          </p>
        </div>
        <button className="btn-ghost" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync from BC'}
        </button>
      </div>

      {stats && (
        <div className={styles.stats}>
          <Stat label="VIP" value={stats.vip} />
          <Stat label="Silver" value={stats.silver} />
          <Stat label="Have the app" value={stats.with_app} />
          {/* The reachable segment: people paying for VIP who have never
              installed the app. Clicking it filters to exactly them. */}
          <Stat
            label="VIP, no app account"
            value={stats.vip_without_app}
            highlight
            onClick={() => { setScheme('VIP'); setHasApp('no'); setExpiry(''); }}
          />
          <Stat label="Blocked" value={stats.blocked} />
        </div>
      )}

      <div className={styles.filters}>
        <input
          className={styles.search}
          placeholder="Search by name, phone, email, or card number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className={styles.select} value={scheme} onChange={(e) => setScheme(e.target.value)}>
          {SCHEMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={styles.select} value={hasApp} onChange={(e) => setHasApp(e.target.value)}>
          {APP_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={styles.select} value={expiry} onChange={(e) => setExpiry(e.target.value)}>
          {EXPIRY_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.tableCard}>
        <div className={styles.scroller}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colName}>Name</th>
                <th>Card No</th>
                <th>Scheme</th>
                <th>Phone</th>
                <th>Expiry</th>
                <th>Points</th>
                <th>App</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {[200, 110, 60, 100, 90, 50, 45].map((w, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 15, width: w, borderRadius: 6 }} /></td>
                  ))}
                </tr>
              ))}

              {!loading && !rows.length && (
                <tr><td colSpan={7} className={styles.empty}>No members match those filters.</td></tr>
              )}

              {!loading && rows.map((m) => (
                <tr key={m.accountNo}>
                  <td className={styles.colName}>
                    <div className={styles.name}>{m.name || '—'}</div>
                    {m.email && <div className={styles.email}>{m.email}</div>}
                  </td>
                  <td><span className={styles.card}>{m.memberCardNo || m.accountNo}</span></td>
                  <td>
                    <span className={`${styles.pill} ${m.scheme === 'VIP' ? styles.pillVip : styles.pillSilver}`}>
                      {m.scheme || '—'}
                    </span>
                  </td>
                  <td className={styles.phone}>{m.phone || '—'}</td>
                  <td className={styles.nowrap}>{fmtDate(m.expiryDate)}</td>
                  <td className={styles.num}>{Number(m.points || 0).toLocaleString()}</td>
                  <td>
                    {m.blocked
                      ? <span className={`${styles.pill} ${styles.pillBlocked}`}>blocked</span>
                      : m.hasApp
                        ? <span className={`${styles.pill} ${styles.pillYes}`}>yes</span>
                        : <span className={styles.muted}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages.toLocaleString()}</span>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight, onClick }) {
  const cls = [styles.stat, highlight ? styles.statHighlight : '', onClick ? styles.statAction : '']
    .filter(Boolean).join(' ');
  return (
    <div className={cls} onClick={onClick}>
      <div className={styles.statValue}>{Number(value || 0).toLocaleString()}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}
